import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../../core/AddButton';
import CancelButton from '../../core/CancelButton';
import Help from '../../core/Help';
import InAppBar from '../../core/InAppBar';
import PrimaryAddButton from '../../core/PrimaryAddButton';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import SearchDBpediaSelect from '../../core/SearchDBpediaSelect';
import SearchSelect from '../../core/SearchSelect';
import { globalStyles as styles } from '../../core/style/gstheme';
import { checkValidForDatatype, getGraphFromProject, getProjectFromGraph, makeCancelable, computeConceptIcon } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import ProjectLink from '../core/ProjectLink';
import SchemeLink from '../core/SchemeLink';

class FindOntologyRelatedConceptDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            offerExternal: false,
            tabValue: 0,
            externalDuplicate: false,
            text: "",
            componentType: ""
        };
    }

    handleClickOpen = async () => {
        try {
            await this.loadData(this.props.componentIRI).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            // If still not loaded might be an edited IRI so check for that
            if (!GS.isLoaded(this.props.componentIRI, this.props.graph)) {
                this.setState({ isErrored: { message: "Cannot load component to search." } });
            }
            else {
                const componentData = GS.data(this.props.componentIRI, this.props.graph);
                const componentType = componentData.type;
                const componentTitle = componentData.title && componentData.title[this.props.viewLang] ?
                    componentData.title[this.props.viewLang]
                    :
                    '';
                this.setState({ open: true, componentType, componentTitle });
            }
        }
        catch (e) {
            this.setState({ isErrored: e });
        }
    };

    loadData(id) {
        this.cancellable = makeCancelable(GS.loadData([id], {}, this.props.graph, true));
        return this.cancellable;
    }

    computeSearch = (value, lang, projectIRI, skosXL) => {
        const projectOnly = this.state.tabValue !== 1 || !this.props.associatedProject || this.props.associatedProject.length === 0;
        const projectGraphs = projectOnly ?
            [getGraphFromProject(projectIRI)]
            :
            this.props.associatedProject.map(ap => getGraphFromProject(ap));
        const search = this.props.matching ?
            [
                {
                    predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                    ...(projectOnly) && {
                        join: [{
                            predicate: "https://grafsync.graphifi.com/schema/gs/hasComponent",
                            value: projectIRI,
                            reverse: true
                        }]
                    }
                }
            ]
            :
            this.state.componentType === "ConceptScheme" ?
                [
                    {
                        predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                        value: this.props.componentIRI
                    }
                ]
                :
                this.state.componentType === "OrderedCollection" ?
                    [
                        {
                            inList: "http://www.w3.org/2004/02/skos/core#memberList",
                            listOf: this.props.componentIRI
                        },
                        {
                            predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                            ...(value !== "") && {
                                value: value,
                                isExactMatch: false
                            }
                        }
                    ]
                    :
                    [
                        {
                            predicate: "http://www.w3.org/2004/02/skos/core#member",
                            value: this.props.componentIRI,
                            reverse: true
                        },
                        {
                            predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                            ...(value !== "") && {
                                value: value,
                                isExactMatch: false
                            }
                        }
                    ];
        let fullSearch =
        {
            search: this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                || this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#Collection")
                || this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection") ?
                [
                    {
                        predicate: "http://purl.org/dc/terms/title",
                        ...(value !== "") && {
                            value: value,
                            isExactMatch: false
                        }
                    }
                ]
                :
                [
                    {
                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        value: "http://www.w3.org/2004/02/skos/core#Concept"
                    },
                    ...search,
                    {
                        predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                        ...(value !== "") && {
                            value: value,
                            isExactMatch: false
                        }
                    }
                ],
            graph: projectGraphs,
            exclude: this.props.exclude,
            pageSize: 20,
            entailment: skosXL ? ["propertyChain"] : [],
            language: lang,
            predicates: [
                "http://www.w3.org/2004/02/skos/core#prefLabel",
                "http://purl.org/dc/terms/title",
                "http://www.w3.org/2002/07/owl#deprecated",
                "https://grafsync.graphifi.com/schema/gs/iri",
                "https://grafsync.graphifi.com/schema/gs/status",
                "http://www.w3.org/2004/02/skos/core#inScheme"
            ]
        };
        if (this.props.searchClasses.length > 0) {
            fullSearch.search.push({
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: this.props.searchClasses
            });
        }
        // For asymmetric props do not allow in results if prop relationship in other direction already
        if (this.props.searchTypes.includes("Asymmetric") && this.props.source && this.props.property) {
            fullSearch.search.push({
                predicate: this.props.property,
                value: this.props.source,
                isText: false,
                invert: true
            });
        }
        return fullSearch;
    };

    includeConcept = (id) => {
        this.props.onIncludeResource(id);
        this.exitFindConcept();
    };

    addExternal = () => this.includeConcept(this.state.externalIRI);

    exitFindConcept = () => this.setState({ open: false, externalDuplicate: false, text: "", tabValue: 0, offerExternal: false });

    declineExternal = () => this.setState({ text: "", offerExternal: false });

    offerExternal = (iri, valid = true) => {
        if (!valid || iri.includes("graphifi")) {
            this.setState({
                externalDuplicate: false,
                offerExternal: false
            });
        }
        else if (this.props.exclude.includes(iri)) {
            this.setState({
                externalDuplicate: true,
                offerExternal: false
            });
        }
        else {
            const isValid = checkValidForDatatype(iri, "http://www.w3.org/2001/XMLSchema#anyURI");
            this.setState({
                externalDuplicate: false,
                offerExternal: isValid,
                externalIRI: iri
            });
        }
    };

    updateSearchText = (text) => this.setState({ text, externalDuplicate: false, offerExternal: false });

    changeTab = (tabValue) => this.setState({ tabValue, externalDuplicate: false, offerExternal: false });

    render() {
        const { classes, viewLang, componentIRI, exclude, associatedProject, linkedData, allowEmpty, allowIRI, matching, useWorkflow } = this.props;
        const { offerExternal, externalDuplicate, tabValue, text, componentTitle } = this.state;

        const defaultToSearch = (tabValue === 2 && (!linkedData || (linkedData && linkedData.length === 0)))
            || (tabValue === 1 && (!associatedProject || (associatedProject && associatedProject.length === 0)));

        const tabItems = [{
            icon: tabValue === 0 || defaultToSearch ? CheckCircleIcon : RadioButtonUncheckedIcon,
            label: componentTitle && !matching ? componentTitle : "Current Project",
        },
        {
            icon: tabValue === 1 ? CheckCircleIcon : RadioButtonUncheckedIcon,
            disabled: associatedProject.length === 0,
            label: "Linked Projects",
        },
        {
            icon: tabValue === 2 ? CheckCircleIcon : RadioButtonUncheckedIcon,
            disabled: !linkedData || !linkedData.includes("DBpedia"),
            label: "DBpedia"
        }];

        return (
            <ProjectConsumer>
                {({ id: projectIRI, defaultLang, skosXL }) => (
                    <React.Fragment>
                        <AddButton dataTest="add-relationship" onClick={() => this.handleClickOpen()} title="Add a new relationship" />
                        <Dialog
                            maxWidth="lg"
                            fullWidth
                            classes={{
                                paper: classes.paper,
                                paperWidthLg: classes.largeDialog
                            }}
                            className={classes.dialog}
                            open={this.state.open}
                            onClose={this.exitFindConcept}
                            aria-labelledby="find-concept-dialog-title">
                            <DialogTitle disableTypography className={classes.mainHeading} id="find-concept-dialog-title">Find Resource</DialogTitle>
                            <DialogContent style={{ height: 400 }}>
                                <Help textRef="FIND_RESOURCE" />
                                <InAppBar
                                    tabValue={tabValue}
                                    background={false}
                                    onChangeTab={this.changeTab}
                                    tabItems={tabItems} />
                                {tabValue === 2 && !defaultToSearch ?
                                    <div style={{ height: 350 }}>
                                        <SearchDBpediaSelect
                                            exclude={exclude}
                                            updateSearchText={this.updateSearchText}
                                            chosen={this.includeConcept}
                                            initialText={text} />
                                    </div>
                                    :
                                    <div style={{ height: 350 }}>
                                        <SearchSelect
                                            dataTest="find-relationship"
                                            focus={true}
                                            exclude={exclude}
                                            allowIRIonNoResults={allowIRI && tabValue === 0}
                                            key={tabValue + "-" + defaultToSearch}
                                            sortByLength={true}
                                            updateSearchText={this.updateSearchText}
                                            associatedProject={tabValue === 1 ? associatedProject : []}
                                            allowEmpty={allowEmpty}
                                            maxLength={250}
                                            initialText={text}
                                            search={(value, lang) => this.computeSearch(value, lang, projectIRI, skosXL)}
                                            listSecondIcon={(result) => computeConceptIcon(result, useWorkflow)}
                                            listSelectionDisplay={(result, lang) => result.prefLabel ?
                                                result.prefLabel[lang] ?
                                                    Array.isArray(result.prefLabel[lang]) ?
                                                        result.prefLabel[lang][0]
                                                        :
                                                        result.prefLabel[lang]
                                                    :
                                                    result.id
                                                :
                                                result.title ?
                                                    result.title[lang] ?
                                                        Array.isArray(result.title[lang]) ?
                                                            result.title[lang][0]
                                                            :
                                                            result.title[lang]
                                                        :
                                                        result.id
                                                    :
                                                    result.id

                                            }
                                            listDisplay={(result, lang) => {
                                                const text = result.prefLabel ?
                                                    result.prefLabel[lang] ?
                                                        Array.isArray(result.prefLabel[lang]) ?
                                                            result.prefLabel[lang].sort().join(" AND ")
                                                            :
                                                            result.prefLabel[lang]
                                                        :
                                                        result.id
                                                    :
                                                    result.title ?
                                                        result.title[lang] ?
                                                            Array.isArray(result.title[lang]) ?
                                                                result.title[lang][0]
                                                                :
                                                                result.title[lang]
                                                            :
                                                            result.id
                                                        :
                                                        result.id;
                                                const isGraphLocked = result.inNamedGraph && GS.isGraphLocked(result.inNamedGraph);
                                                const currentIRI = result.iri ?
                                                    result.iri[result.iri.length - 1]
                                                    :
                                                    result.id;
                                                return <div>
                                                    <Typography variant="body2">
                                                        <span style={{ wordBreak: 'break-word' }}>
                                                            <Tooltip
                                                                PopperProps={{
                                                                    "data-test": "result-tip"
                                                                }}
                                                                title={currentIRI}>
                                                                <span data-test="result-text-words">{text}</span>
                                                            </Tooltip>
                                                        </span>
                                                    </Typography>
                                                    {result.inScheme && !result.inScheme.includes(componentIRI) && result.inScheme.map(scheme =>
                                                        <SchemeLink handleNotAvailable={true} key={scheme} id={scheme} graph={result.inNamedGraph} defaultLang={defaultLang} />
                                                    )}
                                                    {tabValue === 1 &&
                                                        <ProjectLink
                                                            locked={isGraphLocked}
                                                            id={getProjectFromGraph(result.inNamedGraph)}
                                                            defaultLang={defaultLang}
                                                            graph={result.inNamedGraph}
                                                            key={result.id} />
                                                    }
                                                </div>;
                                            }}
                                            sortProp={this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                                                || this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#Collection")
                                                || this.props.searchClasses.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection") ?
                                                "title"
                                                :
                                                "prefLabel"}
                                            errorText={externalDuplicate ? "This external link already exists." : null}
                                            isClickableResult={(result) => !result.invalid && !result.deprecated
                                                && !(result.inNamedGraph && GS.isGraphLocked(result.inNamedGraph))}
                                            title="Find a resource"
                                            onClearResults={this.declineExternal}
                                            onNoResults={allowIRI ? this.offerExternal : null}
                                            lang={viewLang || defaultLang}
                                            chosen={this.includeConcept} />
                                    </div>
                                }
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <CancelButton onClick={this.exitFindConcept} />
                                <PrimaryAddButton title="Add external link" disabled={!offerExternal} onClick={this.addExternal} />
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>
                )}
            </ProjectConsumer>
        );
    }
}

FindOntologyRelatedConceptDialog.defaultProps = {
    exclude: [],
    searchClasses: [],
    associatedProject: [],
    allowEmpty: false,
    searchTypes: [],
    allowIRI: true,
    useWorkflow: false
};

FindOntologyRelatedConceptDialog.propTypes = {
    searchClasses: PropTypes.array,
    graph: PropTypes.string.isRequired,
    allowIRI: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    componentIRI: PropTypes.string.isRequired,
    linkedData: PropTypes.array,
    allowEmpty: PropTypes.bool,
    property: PropTypes.string,
    associatedProject: PropTypes.array,
    associatedProjectGraph: PropTypes.string,
    onIncludeResource: PropTypes.func.isRequired,
    viewLang: PropTypes.string,
    matching: PropTypes.bool,
    exclude: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(FindOntologyRelatedConceptDialog);